import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';

import { Box, Fade, Typography } from '@mui/material';
import { type SxProps, type Theme, keyframes } from '@mui/material/styles';

import { useLocale } from '@shared/hooks/locale';

type Props = {
  isLoading: boolean;
  variant?: 'inline' | 'fullscreen';
};

const MESSAGE_ROTATION_MS = 2500;
const FADE_TIMEOUT_MS = 300;

const GLOW_COLORS = {
  purple: '#BC82F3',
  pink: '#F5B9EA',
  blue: '#8D9FFF',
  coral: '#FF6778',
  orange: '#FFBA71',
  violet: '#C686FF',
};

const rotateGradient = keyframes`
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
`;

const spinnerDash = keyframes`
  0% { stroke-dashoffset: 283; }
  50% { stroke-dashoffset: 70; }
  100% { stroke-dashoffset: 283; }
`;

const spinnerRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Traveling inset shadow that follows the rotating gradient colors
const travelingInsetShadow = keyframes`
  0% {
    box-shadow:
      inset 0 -48px 64px -16px ${GLOW_COLORS.purple}2F,
      inset 0 48px 64px -16px ${GLOW_COLORS.coral}1B;
  }
  16.67% {
    box-shadow:
      inset -48px 0 64px -16px ${GLOW_COLORS.pink}2F,
      inset 48px 0 64px -16px ${GLOW_COLORS.orange}1B;
  }
  33.33% {
    box-shadow:
      inset 0 48px 64px -16px ${GLOW_COLORS.blue}2F,
      inset 0 -48px 64px -16px ${GLOW_COLORS.violet}1B;
  }
  50% {
    box-shadow:
      inset 48px 0 64px -16px ${GLOW_COLORS.coral}2F,
      inset -48px 0 64px -16px ${GLOW_COLORS.purple}1B;
  }
  66.67% {
    box-shadow:
      inset 0 -48px 64px -16px ${GLOW_COLORS.orange}2F,
      inset 0 48px 64px -16px ${GLOW_COLORS.pink}1B;
  }
  83.33% {
    box-shadow:
      inset -48px 0 64px -16px ${GLOW_COLORS.violet}2F,
      inset 48px 0 64px -16px ${GLOW_COLORS.blue}1B;
  }
  100% {
    box-shadow:
      inset 0 -48px 64px -16px ${GLOW_COLORS.purple}2F,
      inset 0 48px 64px -16px ${GLOW_COLORS.coral}1B;
  }
`;

// Smaller traveling shadow for inline variant
const travelingInsetShadowSmall = keyframes`
  0% {
    box-shadow:
      inset 0 -24px 32px -8px ${GLOW_COLORS.purple}27,
      inset 0 24px 32px -8px ${GLOW_COLORS.coral}16;
  }
  16.67% {
    box-shadow:
      inset -24px 0 32px -8px ${GLOW_COLORS.pink}27,
      inset 24px 0 32px -8px ${GLOW_COLORS.orange}16;
  }
  33.33% {
    box-shadow:
      inset 0 24px 32px -8px ${GLOW_COLORS.blue}27,
      inset 0 -24px 32px -8px ${GLOW_COLORS.violet}16;
  }
  50% {
    box-shadow:
      inset 24px 0 32px -8px ${GLOW_COLORS.coral}27,
      inset -24px 0 32px -8px ${GLOW_COLORS.purple}16;
  }
  66.67% {
    box-shadow:
      inset 0 -24px 32px -8px ${GLOW_COLORS.orange}27,
      inset 0 24px 32px -8px ${GLOW_COLORS.pink}16;
  }
  83.33% {
    box-shadow:
      inset -24px 0 32px -8px ${GLOW_COLORS.violet}27,
      inset 24px 0 32px -8px ${GLOW_COLORS.blue}16;
  }
  100% {
    box-shadow:
      inset 0 -24px 32px -8px ${GLOW_COLORS.purple}27,
      inset 0 24px 32px -8px ${GLOW_COLORS.coral}16;
  }
`;

const gradientColors = `${GLOW_COLORS.purple}, ${GLOW_COLORS.pink}, ${GLOW_COLORS.blue}, ${GLOW_COLORS.coral}, ${GLOW_COLORS.orange}, ${GLOW_COLORS.violet}, ${GLOW_COLORS.purple}`;

const borderGradient = `linear-gradient(90deg, ${GLOW_COLORS.purple}40, ${GLOW_COLORS.blue}40, ${GLOW_COLORS.pink}40, ${GLOW_COLORS.violet}40)`;

const circularGradientLayerSx: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '150%',
  paddingBottom: '150%',
  borderRadius: '50%',
};

const VARIANT_CONFIG = {
  fullscreen: {
    spinnerSize: 64,
    gap: 2,
    minHeight: 24,
    shadowAnimation: travelingInsetShadow,
  },
  inline: {
    spinnerSize: 32,
    gap: 1,
    minHeight: 20,
    shadowAnimation: travelingInsetShadowSmall,
  },
} as const;

const RotatingGradientLayers = () => (
  <>
    <Box
      sx={{
        ...circularGradientLayerSx,
        background: `conic-gradient(from 0deg, ${gradientColors})`,
        animation: `${rotateGradient} 4s linear infinite`,
      }}
    />
    <Box
      sx={{
        ...circularGradientLayerSx,
        background: `conic-gradient(from 180deg, ${gradientColors})`,
        animation: `${rotateGradient} 4s linear infinite, ${pulseGlow} 2s ease-in-out infinite`,
      }}
    />
  </>
);

type GlowSpinnerProps = {
  size: number;
  gradientId: string;
};

const GlowSpinner = ({ size, gradientId }: GlowSpinnerProps) => (
  <Box
    sx={{
      width: size,
      height: size,
      animation: `${spinnerRotate} 2s linear infinite`,
    }}
  >
    <svg width={size} height={size} viewBox="0 0 100 100">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={GLOW_COLORS.purple} />
          <stop offset="25%" stopColor={GLOW_COLORS.pink} />
          <stop offset="50%" stopColor={GLOW_COLORS.blue} />
          <stop offset="75%" stopColor={GLOW_COLORS.coral} />
          <stop offset="100%" stopColor={GLOW_COLORS.violet} />
        </linearGradient>
      </defs>
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="283"
        style={{ animation: `${spinnerDash} 1.5s ease-in-out infinite` }}
      />
    </svg>
  </Box>
);

type CmaGenerationProgressMessageProps = {
  isLoading: boolean;
  minHeight: number;
};

export const CmaGenerationProgressMessage = ({
  isLoading,
  minHeight,
}: CmaGenerationProgressMessageProps) => {
  const { t } = useLocale();
  const [messageIndex, setMessageIndex] = useState(0);
  const [showMessage, setShowMessage] = useState(true);

  const messages = useMemo(
    () => [
      t('Analysing market data...'),
      t('Fetching points of interest...'),
      t('Scouting comparable listings in the area...'),
      t('Searching for recent transactions nearby...'),
      t('Calculating optimal asking price...'),
      t('Finalising your report...'),
    ],
    [t],
  );

  useEffect(() => {
    if (!isLoading) {
      setMessageIndex(0);
      setShowMessage(true);
      return;
    }

    const lastMessageIndex = messages.length - 1;

    // Already at the last message, no need to rotate
    if (messageIndex >= lastMessageIndex) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const rotateMessage = () => {
      setShowMessage(false);
      timeoutId = setTimeout(() => {
        setMessageIndex(prev => Math.min(prev + 1, lastMessageIndex));
        setShowMessage(true);
      }, FADE_TIMEOUT_MS);
    };

    const intervalId = setInterval(rotateMessage, MESSAGE_ROTATION_MS);
    return () => {
      clearInterval(intervalId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, messages.length, messageIndex]);

  if (!isLoading) {
    return null;
  }

  return (
    <Fade in={showMessage} timeout={FADE_TIMEOUT_MS}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          textAlign: 'center',
          color: 'text.secondary',
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {messages[messageIndex]}
      </Typography>
    </Fade>
  );
};

export const CmaGenerationProgressCard = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      padding: '2px',
      borderRadius: '14px',
      background: borderGradient,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    }}
  >
    <RotatingGradientLayers />
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        background: 'var(--mui-palette-background-paper)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        animation: `${travelingInsetShadowSmall} 4s linear infinite`,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const CmaGenerationProgress = ({
  isLoading,
  variant = 'inline',
}: Props) => {
  const gradientId = useId();

  if (!isLoading) {
    return null;
  }

  const config = VARIANT_CONFIG[variant];
  const shadowAnimation = `${config.shadowAnimation} 4s linear infinite`;

  const spinnerContent = (
    <>
      <GlowSpinner size={config.spinnerSize} gradientId={gradientId} />
      <CmaGenerationProgressMessage
        isLoading={isLoading}
        minHeight={config.minHeight}
      />
    </>
  );

  if (variant === 'fullscreen') {
    return (
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          padding: '4px',
          background: borderGradient,
          overflow: 'hidden',
        }}
      >
        <RotatingGradientLayers />
        <Box
          sx={theme => ({
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(180deg, #fafbfc 0%, #f0f4f8 100%)',
            ...theme.applyStyles('dark', {
              background: 'linear-gradient(180deg, #272B33 0%, #1E222A 100%)',
            }),
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: config.gap,
            animation: shadowAnimation,
          })}
        >
          {spinnerContent}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          padding: '2px',
          borderRadius: '14px',
          background: borderGradient,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
          overflow: 'hidden',
        }}
      >
        <RotatingGradientLayers />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            background: 'var(--mui-palette-background-paper)',
            borderRadius: '12px',
            py: 1.5,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: config.gap,
            animation: shadowAnimation,
          }}
        >
          {spinnerContent}
        </Box>
      </Box>
    </Box>
  );
};
